// client/src/pages/pmPortal/TenantLedgerPage.js
// The balance is computed from the rows shown beneath it, not read from a stored
// column, so a resident can always add up what they are looking at and get the same
// number. A summary figure that cannot be reconciled to its detail is the thing
// people ring the office about.
import React, { useEffect, useState } from 'react';
import { usePmPortalApiClient } from '../../hooks/usePmPortalApiClient';
import { PageHeader, Panel, Stat, Table, Loading, Notice, Pill, money, fmtDate, titleCase, statusTone }
    from '../../components/secure/pm/PMKit';

const today = () => new Date().toISOString().slice(0, 10);

const TenantLedgerPage = () => {
    const api = usePmPortalApiClient();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get('/api/pm-portal/tenant/ledger').then(setData).catch(e => setError(e.message));
    }, [api]);

    if (error) return <Notice tone="bad" title="Could not load your ledger" message={error} />;
    if (!data) return <Loading />;

    const charged = data.charges.reduce((s, c) => s + Number(c.amount || 0), 0);
    const paid = data.charges.reduce((s, c) => s + Number(c.applied || 0), 0);

    return (
        <>
            <PageHeader
                title="Charges and payments"
                subtitle="Payments are applied to the oldest open charge first. Anything left over is held as prepaid rent."
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <Stat label="Total charged" value={money(charged)} />
                <Stat label="Total applied" value={money(paid)} />
                <Stat
                    label="Balance due"
                    value={money(data.balance_due)}
                    tone={data.balance_due > 0 ? 'warn' : 'good'}
                />
            </div>

            <Panel title="Charges">
                <Table
                    columns={[
                        { key: 'due_on', label: 'Due', nowrap: true, render: r => fmtDate(r.due_on) },
                        { key: 'label', label: 'Charge' },
                        { key: 'charge_type', label: 'Type', render: r => titleCase(r.charge_type) },
                        { key: 'period', label: 'Period', nowrap: true,
                          render: r => (r.period_start ? `${fmtDate(r.period_start)} – ${fmtDate(r.period_end)}` : '—') },
                        { key: 'amount', label: 'Amount', align: 'right', render: r => money(r.amount) },
                        { key: 'applied', label: 'Applied', align: 'right', render: r => money(r.applied) },
                        { key: 'outstanding', label: 'Outstanding', align: 'right',
                          render: r => <span className={r.outstanding > 0 ? 'font-semibold text-rose-700' : ''}>{money(r.outstanding)}</span> },
                        { key: 'status', label: 'Status', render: r => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                    ]}
                    rows={data.charges}
                    rowTone={r => (r.outstanding > 0 && r.due_on < today() ? 'bad' : r.outstanding > 0 ? 'warn' : null)}
                    empty="No charges have been raised on your lease."
                />
            </Panel>

            <Panel title="Payments received">
                <Table
                    columns={[
                        { key: 'entry_date', label: 'Received', nowrap: true, render: r => fmtDate(r.entry_date) },
                        { key: 'memo', label: 'Reference', render: r => r.memo || '—' },
                        { key: 'amount', label: 'Applied', align: 'right', render: r => money(r.amount) },
                    ]}
                    rows={data.payments}
                    empty="No payments have been recorded yet."
                />
            </Panel>

            <p className="text-xs text-slate-500 mt-2">
                To pay online, use Pay Rent (when the office offers it); otherwise contact the office to arrange payment.
                Rent received is held in a special account under 68 IAC 1450.750.
            </p>
        </>
    );
};

export default TenantLedgerPage;
